import { useState, useEffect } from 'react';
import { useSalesPromptDialog } from '@/contexts/SalesPromptDialogContext';
import { useSalesPrompts } from '@/hooks/useSalesPrompts';
import { useBrandManager } from '@/hooks/useBrandManager';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const GlobalSalesPromptDialog = () => {
  const { isOpen, dialogData, closeSalesPromptDialog } = useSalesPromptDialog();
  const { currentBrandId } = useBrandManager();
  const { createPrompt, updatePrompt } = useSalesPrompts(currentBrandId);
  
  const [formData, setFormData] = useState({
    prompt_type: 'executive_summary' as 'executive_summary' | 'scope_items' | 'product_assignment',
    prompt_title: '',
    prompt_content: '',
    is_default: false,
  });

  useEffect(() => {
    if (dialogData) {
      setFormData({
        prompt_type: dialogData.prompt_type,
        prompt_title: dialogData.prompt_title,
        prompt_content: dialogData.prompt_content,
        is_default: dialogData.is_default,
      });
    }
  }, [dialogData]);

  const isEditing = dialogData?.id;

  const handleSubmit = () => {
    if (!currentBrandId) return;

    const data = {
      ...formData,
      brand_id: currentBrandId,
      user_id: currentBrandId,
    };

    if (isEditing) {
      updatePrompt.mutate({ id: dialogData.id!, updates: data });
    } else {
      createPrompt.mutate(data);
    }

    closeSalesPromptDialog();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeSalesPromptDialog();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={isEditing ? "Edit Sales Prompt" : "Create Sales Prompt"}
            popupContext="/sales-operations/prompts"
            isOpen={isOpen}
          >
            {isEditing ? 'Edit Prompt' : 'Create New Prompt'}
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Prompt Type</Label>
            <Select
              value={formData.prompt_type}
              onValueChange={(value: 'executive_summary' | 'scope_items' | 'product_assignment') => 
                setFormData({ ...formData, prompt_type: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="executive_summary">Executive Summary</SelectItem>
                <SelectItem value="scope_items">Scope Items</SelectItem>
                <SelectItem value="product_assignment">Product Assignment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Prompt Title</Label>
            <Input
              value={formData.prompt_title}
              onChange={(e) => setFormData({ ...formData, prompt_title: e.target.value })}
              placeholder="Enter prompt title"
            />
          </div>
          <div>
            <Label>Prompt Content</Label>
            <Textarea
              value={formData.prompt_content}
              onChange={(e) => setFormData({ ...formData, prompt_content: e.target.value })}
              placeholder="Enter the AI prompt instructions"
              rows={10}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_default}
              onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
              className="h-4 w-4"
            />
            <Label>Set as default prompt</Label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeSalesPromptDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
